import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("access_token"));
  const [loading, setLoading] = useState(true);

  // Use the SHARED api instance from lib/api.js — has the 401 refresh interceptor
  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem("access_token");
    if (!currentToken) {
      setUser(null);
      setToken(null);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      // Sync token state with what's actually in localStorage (interceptor may have refreshed it)
      setToken(localStorage.getItem("access_token"));
    } catch (error) {
      console.error("Failed to fetch user:", error);
      // Only logout if there's truly no valid session
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check — AuthCallback handles it
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  // Listen for token refresh events from api.js interceptor
  useEffect(() => {
    const handleTokenRefresh = (e) => {
      const { access_token: newToken, user: refreshedUser } = e.detail || {};
      if (newToken) {
        setToken(newToken);
      }
      if (refreshedUser) {
        setUser(refreshedUser);
      }
    };
    window.addEventListener("auth-token-refreshed", handleTokenRefresh);
    return () => window.removeEventListener("auth-token-refreshed", handleTokenRefresh);
  }, []);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, full_name, role = "learner", tenant_id = null) => {
    const response = await api.post("/auth/register", {
      email, password, full_name, role, tenant_id,
    });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setToken(null);
    setUser(null);
  }, []);

  const refreshToken = async () => {
    const refresh = localStorage.getItem("refresh_token");
    if (!refresh) {
      logout();
      return;
    }

    try {
      const response = await api.post("/auth/refresh", {
        refresh_token: refresh,
      });
      const { access_token, refresh_token: newRefresh, user: userData } = response.data;
      
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", newRefresh);
      setToken(access_token);
      setUser(userData);
    } catch (error) {
      logout();
    }
  };

  const setAuthData = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshToken,
    setAuthData,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "super_admin",
    isTenantAdmin: user?.role === "tenant_admin",
    isTrainer: user?.role === "trainer",
    isLearner: user?.role === "learner",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
